import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Mail, MessageSquare, ShieldOff, CheckCircle2, RefreshCw, Loader2, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—";

const SHUTDOWN_OPTIONS = [
  { label: "3 days", value: 3 },
  { label: "7 days", value: 7 },
  { label: "14 days", value: 14 },
  { label: "30 days", value: 30 },
];

export const ParentZelleOverdueCard = ({ centerActive }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reminderState, setReminderState] = useState({});   // {invoiceId: {email,sms,sending}}
  const [shuttingDown, setShuttingDown] = useState(null);
  const [revoking, setRevoking] = useState(null);
  const [markingPaid, setMarkingPaid] = useState(null);
  const [shutdownDays, setShutdownDays] = useState({});     // {invoiceId: days}

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/parent-communication/zelle-report`);
      setInvoices(res.data.invoices || []);
    } catch {
      toast.error("Unable to load Zelle report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (centerActive) fetchReport(); }, [centerActive]);

  const getToggles = (id) => reminderState[id] ?? { email: true, sms: false };
  const setToggle = (id, field, val) =>
    setReminderState(p => ({ ...p, [id]: { ...getToggles(id), [field]: val } }));

  const sendReminder = async (inv) => {
    const t = getToggles(inv.id);
    if (!t.email && !t.sms) { toast.error("Choose at least Email or Text"); return; }
    setReminderState(p => ({ ...p, [inv.id]: { ...t, sending: true } }));
    try {
      const res = await axios.post(`${API}/parent-communication/send-parent-reminder`, {
        invoiceId: inv.id, sendEmail: t.email, sendSms: t.sms,
      });
      const via = res.data.via?.join(" + ") || "—";
      toast.success(`Reminder sent via ${via}`);
      fetchReport();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unable to send reminder");
    } finally {
      setReminderState(p => ({ ...p, [inv.id]: { ...getToggles(inv.id), sending: false } }));
    }
  };

  const setShutdown = async (inv) => {
    const days = shutdownDays[inv.id] ?? 7;
    setShuttingDown(inv.id);
    try {
      await axios.post(`${API}/parent-communication/set-shutdown`, { invoiceId: inv.id, shutdownDays: days });
      toast.success(`Shutdown set — parent warned. Access ends in ${days} day${days !== 1 ? "s" : ""}.`);
      fetchReport();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unable to set shutdown");
    } finally {
      setShuttingDown(null);
    }
  };

  const revokeNow = async (inv) => {
    if (!window.confirm(`Revoke ${inv.parentName}'s portal access immediately?`)) return;
    setRevoking(inv.id);
    try {
      const childId = (inv.childIds || [])[0];
      await axios.post(`${API}/parent-communication/revoke-access`, { childId });
      await axios.post(`${API}/parent-communication/mark-invoice-paid`, { invoiceId: inv.id }); // close the invoice
      toast.success("Access revoked");
      fetchReport();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unable to revoke");
    } finally {
      setRevoking(null);
    }
  };

  const markPaid = async (inv) => {
    setMarkingPaid(inv.id);
    try {
      await axios.post(`${API}/parent-communication/mark-invoice-paid`, { invoiceId: inv.id });
      toast.success("Marked as paid — removed from report");
      fetchReport();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unable to mark paid");
    } finally {
      setMarkingPaid(null);
    }
  };

  if (!centerActive) return null;

  return (
    <Card className="overflow-hidden border-2 border-orange-100 bg-white shadow-xl" data-testid="zelle-overdue-card">
      <CardHeader className="bg-gradient-to-r from-orange-50 to-amber-50">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-orange-800" data-testid="zelle-report-title">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            Zelle Payment Status
            {invoices.length > 0 && (
              <Badge className="bg-orange-500 text-white text-xs" data-testid="zelle-overdue-count">
                {invoices.length} overdue
              </Badge>
            )}
          </CardTitle>
          <Button variant="outline" size="sm" onClick={fetchReport} disabled={loading} data-testid="zelle-report-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="p-4 space-y-4 max-h-[600px] overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-orange-400" /></div>
        ) : invoices.length === 0 ? (
          <div className="text-center py-10 text-slate-400" data-testid="zelle-report-empty">
            <CheckCircle2 className="mx-auto h-8 w-8 mb-2 text-emerald-400" />
            <p className="text-sm font-medium text-emerald-600">All Zelle accounts current</p>
          </div>
        ) : (
          invoices.map((inv) => {
            const toggles = getToggles(inv.id);
            const isSending = toggles.sending;
            const days = shutdownDays[inv.id] ?? 7;

            return (
              <div key={inv.id}
                className="rounded-2xl border border-orange-200 bg-orange-50 p-4 space-y-3"
                data-testid={`zelle-invoice-${inv.id}`}>

                {/* Header row */}
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-bold text-slate-800">{inv.parentName}</p>
                    <p className="text-xs text-slate-500">{(inv.childNames || []).join(", ") || "—"}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{inv.parentEmail}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-lg font-black text-slate-800">${(inv.amount || 0).toFixed(2)}</p>
                    <p className="text-xs text-orange-600 font-semibold">
                      {inv.daysLate > 0 ? `${inv.daysLate}d overdue` : "Billed today"}
                    </p>
                    <p className="text-[10px] text-slate-400">Billed {fmt(inv.invoiceDate)}</p>
                  </div>
                </div>

                {/* Shutdown countdown */}
                {inv.shutdownAt && (
                  <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-1.5" data-testid={`shutdown-countdown-${inv.id}`}>
                    <Clock className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />
                    <p className="text-xs text-red-700 font-semibold">
                      {inv.daysUntilShutdown === 0
                        ? "Access revokes today"
                        : `Access revokes in ${inv.daysUntilShutdown} day${inv.daysUntilShutdown !== 1 ? "s" : ""} — ${fmt(inv.shutdownAt)}`}
                    </p>
                  </div>
                )}

                {/* Reminder toggles + send */}
                <div className="flex items-center gap-2 flex-wrap">
                  <label className={`flex items-center gap-1 cursor-pointer px-2 py-1 rounded-lg border text-[11px] font-semibold transition ${toggles.email ? "border-blue-400 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-400"}`}
                    data-testid={`zelle-email-toggle-${inv.id}`}>
                    <input type="checkbox" className="hidden" checked={toggles.email} onChange={e => setToggle(inv.id, "email", e.target.checked)} />
                    <Mail className="w-3 h-3" /> Email
                  </label>
                  <label className={`flex items-center gap-1 cursor-pointer px-2 py-1 rounded-lg border text-[11px] font-semibold transition ${toggles.sms ? "border-purple-400 bg-purple-50 text-purple-700" : "border-slate-200 text-slate-400"}`}
                    data-testid={`zelle-sms-toggle-${inv.id}`}>
                    <input type="checkbox" className="hidden" checked={toggles.sms} onChange={e => setToggle(inv.id, "sms", e.target.checked)} />
                    <MessageSquare className="w-3 h-3" /> Text
                  </label>
                  <Button type="button" size="sm" variant="outline"
                    className="h-7 rounded-full border-blue-300 text-blue-700 hover:bg-blue-50 px-3 text-[11px]"
                    disabled={isSending}
                    onClick={() => sendReminder(inv)}
                    data-testid={`send-reminder-btn-${inv.id}`}>
                    {isSending ? <Loader2 className="h-3 w-3 animate-spin" /> : "Send Reminder"}
                  </Button>
                  {inv.reminderSentAt && (
                    <span className="text-[10px] text-slate-400">Last sent {fmt(inv.reminderSentAt)}</span>
                  )}
                </div>

                {/* Shutdown in X days + Revoke + Mark Paid */}
                <div className="flex items-center gap-2 flex-wrap">
                  <select
                    value={days}
                    onChange={e => setShutdownDays(p => ({ ...p, [inv.id]: parseInt(e.target.value) }))}
                    className="h-7 rounded-lg border border-slate-200 text-[11px] px-2 text-slate-700 bg-white"
                    data-testid={`shutdown-days-select-${inv.id}`}>
                    {SHUTDOWN_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>Shutdown in {o.label}</option>
                    ))}
                  </select>
                  <Button type="button" size="sm" variant="outline"
                    className="h-7 rounded-full border-amber-300 text-amber-700 hover:bg-amber-50 px-3 text-[11px]"
                    disabled={shuttingDown === inv.id}
                    onClick={() => setShutdown(inv)}
                    data-testid={`set-shutdown-btn-${inv.id}`}>
                    {shuttingDown === inv.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Clock className="h-3 w-3 mr-1" />Set</>}
                  </Button>
                  <Button type="button" size="sm" variant="outline"
                    className="h-7 rounded-full border-red-300 text-red-600 hover:bg-red-50 px-3 text-[11px]"
                    disabled={revoking === inv.id}
                    onClick={() => revokeNow(inv)}
                    data-testid={`revoke-now-btn-${inv.id}`}>
                    {revoking === inv.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <><ShieldOff className="h-3 w-3 mr-1" />Revoke Now</>}
                  </Button>
                  <Button type="button" size="sm" variant="outline"
                    className="h-7 rounded-full border-emerald-300 text-emerald-700 hover:bg-emerald-50 px-3 text-[11px]"
                    disabled={markingPaid === inv.id}
                    onClick={() => markPaid(inv)}
                    data-testid={`mark-paid-btn-${inv.id}`}>
                    {markingPaid === inv.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <><CheckCircle2 className="h-3 w-3 mr-1" />Paid</>}
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
};
